import { useMemo } from "react"
import { usePlantReport } from "../scripts/queries/usePlantReportQueries";
import { usePlantFilters, searchRows, usePlantTable, sortedRows, exportPlantTables } from "./usePlantTable";

const EMPTY_ARRAY = [];

const MACHINE_COLUMNS = [
    {accessorKey: 'maquina', header: 'Máquina', type: 'text', width: 220},
    {accessorKey: 'arregloReal', header: 'Hrs arreglo real', type: 'dec'},
    {accessorKey: 'prodReal', header: 'Hrs prod real', type: 'dec'},
    {accessorKey: 'improdReal', header: 'Hrs improd real', type: 'dec'},
    {accessorKey: 'totalHoras', header: 'Total horas máq', type: 'dec'},
    {accessorKey: 'noTripuladas', header: 'Hrs máq no trip ponde', type: 'dec', width: 190},
    {accessorKey: 'eficiencia', header: 'Máq efi pond', type: 'pct'},
    {accessorKey: 'diferencia', header: 'Diferencia en horas', type: 'dec', width: 170},
];
//The "Total eficiencia planta" row shows the plant efficiency and captura in the last two columns
const TOTAL_HEADERS = {maquina: 'Total eficiencia planta', eficiencia: 'Eficiencia planta', diferencia: 'Captura planta'};
const TOTAL_COLUMNS = MACHINE_COLUMNS.map(col => ({
    ...col,
    header: TOTAL_HEADERS[col.accessorKey] ?? col.header,
    type: col.accessorKey === 'diferencia' ? 'pct' : col.type,
}));
const SUM_FIELDS = ['arregloReal', 'prodReal', 'improdReal', 'totalHoras', 'noTripuladas'];

//Data eficiencia of a day grouped by machine. 
export const useMachineDayNormalizer = (active = true) => {
    const filters = usePlantFilters('MACHINE_DAY_FILTERS');
    const {fecha, search, sorting, setSorting} = filters;

    const {data: report, refetch: fetchData, isFetching, isLoading: loading} = usePlantReport(fecha, active);
    const rows = report?.filas ?? EMPTY_ARRAY;

    const pivot = useMemo(() => {
        const machines = {};
        rows.forEach(row => {
            const key = row.maquina.trim().replace(/\s+/g, ' ').toUpperCase();
            const machine = machines[key] ??= {maquina: key, arregloReal: 0, prodReal: 0, improdReal: 0, totalHoras: 0, noTripuladas: 0, eficiencia: 0, diferencia: 0};
            machine.arregloReal += row.hrsArregloReal;
            machine.prodReal += row.hrsProdReal;
            machine.improdReal += row.hrsImprodReal;
            machine.totalHoras += row.totalHorasMaq;
            machine.noTripuladas += row.horasMaqNoTripPond;
            machine.eficiencia += row.eficienciaMaqPond ?? 0; //Blank when the machine row is at 250% or more
            machine.diferencia += row.diferenciaHoras;
        });
        return Object.values(machines);
    }, [rows]);

    const filteredPivot = useMemo(() => searchRows(pivot, search, ['maquina']), [pivot, search]);

    //Sums of every machine, with the plant efficiency and captura calculated by the backend
    const totalData = useMemo(() => report ? [{
        maquina: 'Total',
        ...Object.fromEntries(SUM_FIELDS.map(field => [field, pivot.reduce((sum, machine) => sum + machine[field], 0)])),
        eficiencia: report.planta.eficiencia,
        diferencia: report.planta.captura,
    }] : EMPTY_ARRAY, [report, pivot]);

    const {columns, table} = usePlantTable(MACHINE_COLUMNS, filteredPivot, sorting, setSorting);
    const {columns: totalColumns, table: totalTable} = usePlantTable(TOTAL_COLUMNS, totalData, EMPTY_ARRAY);

    const exportCSV = () => exportPlantTables(`Reporte_Maquina_Dia_${fecha}.xlsx`, [
        {columns, rows: sortedRows(table)},
        ...(totalData.length ? [{columns: totalColumns, rows: totalData}] : []),
    ]);

    return {...filters, table, extraTables: [{table: totalTable}], loading, isFetching, fetchData, exportCSV};
}